import { supabase } from '../../lib/supabase';
import { Emenda, EmendaDocumento } from '../types/emenda';

export const emendaService = {
  async getAll(): Promise<Emenda[]> {
    const { data, error } = await supabase
      .from('emendas')
      .select(`
        *,
        documentos:emenda_documentos(*)
      `)
      .order('data_publicacao', { ascending: false });

    if (error) {
      console.error('Error fetching emendas:', error);
      throw error;
    }

    return data || [];
  },

  async getById(id: string): Promise<Emenda> {
    const { data, error } = await supabase
      .from('emendas')
      .select(`
        *,
        documentos:emenda_documentos(*)
      `)
      .eq('id', id)
      .single();

    if (error) {
      console.error(`Error fetching emenda ${id}:`, error);
      throw error;
    }

    return data;
  },

  async create(emenda: Omit<Emenda, 'id' | 'created_at' | 'documentos'>): Promise<Emenda> {
    const { data, error } = await supabase
      .from('emendas')
      .insert([emenda])
      .select()
      .single();

    if (error) {
      console.error('Error creating emenda:', error);
      throw error;
    }

    return data;
  },

  async update(id: string, emenda: Partial<Omit<Emenda, 'id' | 'created_at' | 'documentos'>>): Promise<Emenda> {
    const { data, error } = await supabase
      .from('emendas')
      .update(emenda)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error(`Error updating emenda ${id}:`, error);
      throw error;
    }

    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('emendas')
      .delete()
      .eq('id', id);

    if (error) {
      console.error(`Error deleting emenda ${id}:`, error);
      throw error;
    }
  },

  async uploadImage(file: File): Promise<string> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `emendas/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('emendas-imagens')
      .upload(filePath, file);

    if (uploadError) {
      console.error('Error uploading image:', uploadError);
      throw uploadError;
    }

    const { data } = supabase.storage
      .from('emendas-imagens')
      .getPublicUrl(filePath);

    return data.publicUrl;
  },

  async uploadDocument(file: File): Promise<string> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `documentos/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('emendas-documentos')
      .upload(filePath, file);

    if (uploadError) {
      console.error('Error uploading document:', uploadError);
      throw uploadError;
    }

    const { data } = supabase.storage
      .from('emendas-documentos')
      .getPublicUrl(filePath);

    return data.publicUrl;
  },

  async addDocument(emendaId: string, nome: string, url: string): Promise<EmendaDocumento> {
    const { data, error } = await supabase
      .from('emenda_documentos')
      .insert([{ emenda_id: emendaId, nome, arquivo_url: url }])
      .select()
      .single();

    if (error) {
      console.error('Error adding document:', error);
      throw error;
    }

    return data;
  },

  async removeDocument(id: string, url: string): Promise<void> {
    // Optional: Delete from storage
    if (url.includes('emendas-documentos')) {
       const path = url.split('emendas-documentos/').pop();
       if (path) {
         // Some storage implementations might require full path or handle public URLs differently
         // For now let's just delete the DB record
       }
    }

    const { error } = await supabase
      .from('emenda_documentos')
      .delete()
      .eq('id', id);

    if (error) {
      console.error(`Error deleting document ${id}:`, error);
      throw error;
    }
  }
};
